import { Action, Reducer } from 'redux';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../';
import { ToxnotServiceClient } from '../../ToxnotServiceClient';
import * as Models from '../../../store/Models/Supplier/Supplier';
import { Questionnaire, SurveyTemplateDataModel, SustainabilityAndCircularityIds, VocIds } from '../../Models/Surveys/SurveyDataModel';
import { AppServices } from '../../../AppServices';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import * as Utilities from '../../../utilities/Utilities';
import { DynamicRegulation } from '../../Models/Compliance/DynamicRegulation';

// ===============================================================================

//
// State definition
//

export enum InvitingSuppliersStatus {
    Normal,
    InviteFailed,
    InviteFailedInvalidEmailToken,
    StartingInvitation,
    InvitationSent
}

export enum InviteSuppliersViewType {
    Main,
    ExceedQuotaWarning,
    InvitingSuppliers
}

export enum CreateSurveysMode {
    Send = 0,
    Draft
}

interface RequestedDataSectionBase {
    friendlyDescription: string;
}

export interface RequestedDataSection extends RequestedDataSectionBase {
    type: RequestedDataSectionType;
}

export enum RequestedDataSectionType {
    MaterialDisclosure = 0,
    Compliance,
    Circularity,
    Sustainability,
    DocsAndCerts,
    CustomQuestionnaires,
    Reminders,
    CompletionInstructions,
    RequestInstructions,
    Languages,
    Voc
}

export interface InviteSuppliersState {
    isLoading: boolean;
    activeView: InviteSuppliersViewType;
    invitingSuppliersStatus: InvitingSuppliersStatus;
    offlineProcessOperationId: string | null;
    requestedDataSections: { [type: number]: string[] };
    templates: SurveyTemplateDataModel[];
    questionnaires: Questionnaire[];
    dynamiceRegulations: DynamicRegulation[];
    currentlySelectedTemplateId: string | null;
    columnIdsToFriendlyNames: { [columnId: string]: string }
}

const getInitialState = (): InviteSuppliersState => {
    return {
        isLoading: true,
        activeView: InviteSuppliersViewType.Main,
        invitingSuppliersStatus: InvitingSuppliersStatus.Normal,
        offlineProcessOperationId: null,
        requestedDataSections: {},
        templates: [],
        currentlySelectedTemplateId: null,
        questionnaires: [],
        dynamiceRegulations: [],
        columnIdsToFriendlyNames: {},
    }
}

// ===============================================================================

//
// Action definitions
//

const UpdateInviteSuppliersState = "UPDATE_INVITE_SUPPLIERS_STATE";

interface UpdateInviteSuppliersStateAction {
    type: typeof UpdateInviteSuppliersState;
    value: Partial<InviteSuppliersState>;
}

type KnownAction = UpdateInviteSuppliersStateAction;

const toxnotServiceClient = new ToxnotServiceClient();

// ===============================================================================

//
// Helper Functions
//

export function getCurrentlySelectedTemplateFromState(inviteSuppliersState: InviteSuppliersState) {
    if (inviteSuppliersState.currentlySelectedTemplateId === null) {
        throw new Error("ERROR in getCurrentlySelectedTemplateFromState - Currently selected template id was null.");
    }

    let template = inviteSuppliersState.templates.firstOrDefault(t => t.templateId === inviteSuppliersState.currentlySelectedTemplateId);

    if (!template) {
        template = inviteSuppliersState.templates.length > 0 ? inviteSuppliersState.templates[0] : null;
    }

    return template;
}

// ===============================================================================


function verifyAndGetInviteSuppliersState(getState: () => ApplicationState) {
    const appState = getState();

    if (!appState.inviteSuppliersState) {
        throw new Error("ERROR - Supplier State was not defined.");
    }

    return appState.inviteSuppliersState;
}

// ===============================================================================

export function getContactEmailsAsString(supplier: Models.Supplier) {
    return supplier.contactList.map(c => c.email).join(",");
}

// ===============================================================================

export function getQuestionnaireNamesFromTemplate(template: SurveyTemplateDataModel, questionnaires: Questionnaire[]) {
    const ret: string[] = [];

    const bomQuestionnaire = questionnaires.find(q => template.bomQuestionnaireId === q.questionnaireId);
    const supplierQuestionnaire = questionnaires.find(q => template.supplierQuestionnaireId === q.questionnaireId);
    const attestationQuestionnaire = questionnaires.find(q => template.attestationQuestionnaireId === q.questionnaireId)

    if (bomQuestionnaire) {
        ret.push(bomQuestionnaire.name);
    }

    if (supplierQuestionnaire) {
        ret.push(supplierQuestionnaire.name);
    }

    if (attestationQuestionnaire) {
        ret.push(attestationQuestionnaire.name);
    }

    return ret;
}

// ===============================================================================

function getRequestedDataFromTemplateId(
    templateId: string,
    allTemplates: SurveyTemplateDataModel[],
    questionnaires: Questionnaire[],
    appServices: AppServices
) {
    const ret: { [type: number]: string[] } = {};

    const template = allTemplates.find(t => t.templateId === templateId);

    if (!template) {
        return ret;
    }

    const requestedData = template.dataRequested;

    if (requestedData.materialDisclosureAsked) {
        ret[RequestedDataSectionType.MaterialDisclosure] = template.bomColumns.map(c => c.columnId);
    }

    if (requestedData.complianceList.length > 0) {
        ret[RequestedDataSectionType.Compliance] = requestedData.complianceList;
    }

    if (requestedData.circularityDataAsked) {
        ret[RequestedDataSectionType.Circularity] = [
            SustainabilityAndCircularityIds.EndOfLifeOptions,
            SustainabilityAndCircularityIds.Contact,
            SustainabilityAndCircularityIds.Instructions
        ];
    }

    if (requestedData.sustainabilityDataAsked) {
        const specifics: string[] = [];

        if (requestedData.embodiedCarbonDataAsked) {
            specifics.push(SustainabilityAndCircularityIds.EmbodiedCarbon);
        }

        if (requestedData.waterUseDataAsked) {
            specifics.push(SustainabilityAndCircularityIds.WaterUsageData);
        }

        if (requestedData.carbonTransparencyDataAsked) {
            specifics.push(SustainabilityAndCircularityIds.CarbonTransparency);
        }

        if (requestedData.carbonOtherDataAsked) {
            specifics.push(SustainabilityAndCircularityIds.CarbonOther);
        }

        ret[RequestedDataSectionType.Sustainability] = specifics;
    }

    if (requestedData.vocDataAsked) {
        const specifics: string[] = [];

        if (requestedData.vocTransparencyDataAsked) {
            specifics.push(VocIds.VocTransparency);
        }

        if (requestedData.vocOtherDataAsked) {
            specifics.push(VocIds.VocOther);
        }

        ret[RequestedDataSectionType.Voc] = specifics;
    }

    if (requestedData.documentsCertificationList.length > 0) {
        ret[RequestedDataSectionType.DocsAndCerts] = requestedData.documentsCertificationList.map(d => d.documentCertificationType);
    }

    if (template.requestInstructions !== "") {
        ret[RequestedDataSectionType.RequestInstructions] = [template.requestInstructions];
    }

    if (template.completionInstructions !== "") {
        ret[RequestedDataSectionType.CompletionInstructions] = [template.completionInstructions];
    }

    if (template.bomQuestionnaireId !== null || template.attestationQuestionnaireId !== null || template.supplierQuestionnaireId !== null) {
        ret[RequestedDataSectionType.CustomQuestionnaires] = getQuestionnaireNamesFromTemplate(template, questionnaires);
    }

    if (template.scheduledReminders.length > 0) {
        ret[RequestedDataSectionType.Reminders] = template.scheduledReminders.map(r => "tn-reminder-" + r.scheduledDaysSinceRequestSent.toString());
    }

    return ret;
}

// ===============================================================================

export function removeRowIdentifiersFromSuppliersToInvite(suppliersToInvite: Models.InviteSupplierRowModel[]): Models.InviteSupplierModel[] {
    return suppliersToInvite.map(s => (
        {
            supplierId: s.supplierId,
            supplierName: s.supplierName,
            contactEmail: s.contactEmail,
            requestedMaterials: s.requestedMaterials
        }
    ));
}

// ===============================================================================

async function initializeState(dispatch: (action: UpdateInviteSuppliersStateAction) => void, getState: () => ApplicationState ) {
    const state = getState();

    if (!state || !state.appServices || !state.inviteSuppliersState) {
        throw new Error("Error: Invite Suppliers was not in the correct state.");
    }

    dispatch({ type: UpdateInviteSuppliersState, value: { isLoading: true } });

    const apiResult = await toxnotServiceClient.inviteSuppliersBundleGet();

    if (!apiResult.success || apiResult.data === null) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    const defaultTemplateId = apiResult.data.defaultTemplateId;

    dispatch({
        type: UpdateInviteSuppliersState,
        value: {
            isLoading: false,
            templates: apiResult.data.templates,
            questionnaires: apiResult.data.questionnaires,
            dynamiceRegulations: apiResult.data.dynamicRegulations,
            requestedDataSections: getRequestedDataFromTemplateId(defaultTemplateId, apiResult.data.templates, apiResult.data.questionnaires, state.appServices),
            currentlySelectedTemplateId: defaultTemplateId,
            columnIdsToFriendlyNames: apiResult.data.columnIdsToFriendlyNames
        }
    });
}

// ===============================================================================

async function willUserGoOverMaxLibraryQuota(appServices: AppServices, suppliersToInvite: Models.InviteSupplierModel[]): Promise<boolean | null> {
    if (appServices.userContext == null) {
        throw new Error("User Context Was Null");
    }

    let potentialNumberOfAddedMaterials = 0;

    for (let s of suppliersToInvite) {
        let newMaterials = s.requestedMaterials;
        potentialNumberOfAddedMaterials += newMaterials.filter(n => n.inventoryId !== null).length;
    }

    const apiResult = await toxnotServiceClient.inventoriesCountLibraryBoms();

    if (!apiResult.success || apiResult.data == null) {
        appServices.displayApiErrorMessage();
        return null;
    }

    if ((apiResult.data + potentialNumberOfAddedMaterials) > appServices.userContext.userCapabilities.maxLibraryInventories) {
        return true;
    }

    return false;
}

// ===============================================================================

function validateAllData(supplierRowModels: Models.InviteSupplierRowModel[], appServices: AppServices): boolean {
    if (supplierRowModels.some(s => (s.supplierName.trim() === "") || (s.contactEmail.trim() === ""))) {
        const alertText = appServices.localizeString("The company name and email address fields cannot be blank.");
        alert(alertText);
        return false;
    }

    if (supplierRowModels.length === 0) {
        alert(appServices.localizeString("At least one supplier must be provided."));
        return false;
    }

    if (supplierRowModels.map(s => s.supplierName.trim()).getDuplicates().length > 0) {
        $('#duplicate-supplier-name-warning-modal').modal('show');
        return false;
    }

    if (supplierRowModels.some(s => !Utilities.isValidEmailAddress(s.contactEmail))) {
        alert(appServices.localizeString("One or more of the provided contact emails is invalid."));
        return false;
    }

    if (supplierRowModels.every(s => s.requestedMaterials.length === 0)) {
        if (!confirm(appServices.localizeString("You haven't specified any materials. Requests work best when you ask a Supplier for specific materials - but if you don't know your material names, your supplier will be able to respond with any materials they want."))) {
            return false;
        }
    }

    return true;
}

// ===============================================================================

export function isSupplierOnlyMode(inviteSuppliersState: InviteSuppliersState) {
    const template = getCurrentlySelectedTemplateFromState(inviteSuppliersState);

    if (!template) {
        return false;
    }

    if (Object.keys(inviteSuppliersState.requestedDataSections).length === 0 &&
        template.bomQuestionnaireId === null &&
        template.supplierQuestionnaireId !== null
    ) {
        return true;
    }

    return false;
}

// ===============================================================================

async function createAndSendSurveys(getState: () => ApplicationState, supplierRowModels: Models.InviteSupplierRowModel[], dispatch: (action: KnownAction) => void) {

    const appState = getState();
    const inviteSuppliersState = verifyAndGetInviteSuppliersState(getState);

    dispatch({
        type: UpdateInviteSuppliersState,
        value: {
            activeView: InviteSuppliersViewType.InvitingSuppliers,
            offlineProcessOperationId: null,
            invitingSuppliersStatus: InvitingSuppliersStatus.StartingInvitation
        }
    });

    const userContext = appState.appServices!.userContext!;
    const reducedUserContext = { ownerId: userContext.teamOwnerId, email: userContext.emailAddress };

    if (userContext.fullName.trim() === "") {
        throw new Error("The Requestor name was not valid.")
    }

    // if we are in supplier only mode, clear all provided materials...
    if (isSupplierOnlyMode(inviteSuppliersState)) {
        supplierRowModels = supplierRowModels.map(s => ({ ...s, requestedMaterials: [] }));
    }

    appState.appServices?.trackEvent("Begin_initializeCreateAndSendSurveysOfflineProcess", reducedUserContext);

    const apiResult = await toxnotServiceClient.initializeCreateAndSendSurveysOfflineProcess({
        suppliersToInvite: removeRowIdentifiersFromSuppliersToInvite(supplierRowModels),
        mode: CreateSurveysMode.Send,
        requestedDataSections: inviteSuppliersState.requestedDataSections,
        templateIdToApply: inviteSuppliersState.currentlySelectedTemplateId
    });

    appState.appServices?.trackEvent("End_initializeCreateAndSendSurveysOfflineProcess", reducedUserContext);

    if (apiResult.result == "InvalidEmailToken") {
        dispatch({ type: UpdateInviteSuppliersState, value: { invitingSuppliersStatus: InvitingSuppliersStatus.InviteFailedInvalidEmailToken } });
        return;
    }

    else if (!apiResult.success || apiResult.data === null) {
        dispatch({ type: UpdateInviteSuppliersState, value: { invitingSuppliersStatus: InvitingSuppliersStatus.InviteFailed } });
        return;
    }

    const offlineProcessOperationId = apiResult.data.operationId;

    dispatch({
        type: UpdateInviteSuppliersState,
        value: {
            activeView: InviteSuppliersViewType.InvitingSuppliers,
            invitingSuppliersStatus: InvitingSuppliersStatus.InvitationSent,
            offlineProcessOperationId: offlineProcessOperationId
        }
    });
}

// ===============================================================================

//
// Action creators
//

export const actionCreators = {
    initialize: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        initializeState(dispatch, getState);
    },
    resetState: (doFullInitalization: boolean = true): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({
            type: UpdateInviteSuppliersState,
            value: getInitialState()
        });

        if (doFullInitalization) {
            initializeState(dispatch, getState);
        }
    },
    updateSuppliersToInviteStatus: (newStatus: InvitingSuppliersStatus): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const supplierState = verifyAndGetInviteSuppliersState(getState);

        dispatch({ type: UpdateInviteSuppliersState, value: { invitingSuppliersStatus: newStatus } });
    },
    updateActiveView: (newActiveView: InviteSuppliersViewType): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const supplierState = verifyAndGetInviteSuppliersState(getState);

        dispatch({ type: UpdateInviteSuppliersState, value: { activeView: newActiveView } });
    },
    inviteSuppliers: (mode: CreateSurveysMode, overrideMaxLibrayCheck?: boolean): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        overrideMaxLibrayCheck ??= false;

        const state = getState();

        const inviteSuppliersState = state.inviteSuppliersState;

        if (!state.appServices || !inviteSuppliersState || !state.selectSuppliersMaterialsTableState) {
            return;
        }

        const supplierRowModels = state.selectSuppliersMaterialsTableState.supplierRowModels;

        // Make sure user has valid data.
        if (!validateAllData(supplierRowModels, state.appServices)) {
            return;
        }
        // Make sure user won't go over quota
        if (!overrideMaxLibrayCheck) {
            const result = await willUserGoOverMaxLibraryQuota(state.appServices, supplierRowModels);

            // error 
            if (result == null) {
                return;
            }
            // user will go over quota
            else if (result) {
                dispatch({
                    type: UpdateInviteSuppliersState,
                    value: {
                        activeView: InviteSuppliersViewType.ExceedQuotaWarning
                    }
                });

                return;
            }
        }

        state.appServices.trackEvent("get-started-send-requests-immediately-button-clicked");

        switch (mode) {
            case CreateSurveysMode.Send:
                createAndSendSurveys(getState, supplierRowModels, dispatch);
                break;
            default:
                throw new Error("Unsupported Create Surveys Mode");
        }
    },
    updateInviteSuppliersState: (inviteSuppliersStateToUpdate: Partial<InviteSuppliersState>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const inviteSuppliersState = verifyAndGetInviteSuppliersState(getState);

        const newInviteSuppliersState = {
            ...inviteSuppliersState,
            ...inviteSuppliersStateToUpdate
        };

        dispatch({
            type: UpdateInviteSuppliersState,
            value: newInviteSuppliersState
        });
    },
    updateCurrentlySelectedTemplate: (templateId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const inviteSuppliersState = verifyAndGetInviteSuppliersState(getState);

        dispatch({
            type: UpdateInviteSuppliersState,
            value: {
                currentlySelectedTemplateId: templateId,
                requestedDataSections: getRequestedDataFromTemplateId(templateId, inviteSuppliersState.templates, inviteSuppliersState.questionnaires, getState().appServices!)
            }
        });
    },
};

// ===============================================================================

//
// Reducer
//

export const reducer: Reducer<InviteSuppliersState> = (state: InviteSuppliersState | undefined, action: Action): InviteSuppliersState => {
    if (state === undefined) {
        return getInitialState();
    }

    switch (action.type) {
        case UpdateInviteSuppliersState:
            return {
                ...state,
                ...(action as UpdateInviteSuppliersStateAction).value
            };
    }
   
    return state;
}
